// Authentication service for Azure AD B2C
// Handles login, logout, token management, and user state

const API_BASE_URL = '/api/auth';

class AuthService {
  constructor() {
    this.token = localStorage.getItem('auth_token');
    this.user = null;
    this.isAuthenticated = false;
    this.isAdmin = false;

    // Initialize user state from token
    if (this.token) {
      this.validateToken();
    }
  }

  async login() {
    try {
      // Get authorization URL from backend
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to initiate login');
      }

      // Open Azure AD B2C login in a popup or redirect
      window.location.href = data.auth_url;

    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  async handleCallback(authCode, redirectUri) {
    try {
      const response = await fetch(`${API_BASE_URL}/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: authCode,
          redirect_uri: redirectUri,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Authentication failed');
      }

      // Store token and user info
      this.token = data.token;
      this.user = data.user;
      this.isAuthenticated = true;
      this.isAdmin = data.user.is_admin || false;

      localStorage.setItem('auth_token', this.token);

    } catch (error) {
      console.error('Callback handling failed:', error);
      throw error;
    }
  }

  async logout() {
    try {
      // Call backend logout (optional, mainly for server-side cleanup)
      const response = await fetch(`${API_BASE_URL}/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        // Clear local state
        this.clearAuthState();
        // Redirect to Azure logout URL
        window.location.href = data.logout_url;
      } else {
        // Clear local state anyway
        this.clearAuthState();
      }

    } catch (error) {
      console.error('Logout failed:', error);
      // Clear local state anyway
      this.clearAuthState();
    }
  }

  async validateToken() {
    if (!this.token) {
      this.clearAuthState();
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success && data.valid) {
        this.user = data.user;
        this.isAuthenticated = true;
        this.isAdmin = data.user.is_admin || false;
      } else {
        this.clearAuthState();
        return false;
      }

    } catch (error) {
      console.error('Token validation failed:', error);
      this.clearAuthState();
      return false;
    }
  }

  async getCurrentUser() {
    if (!this.token) return null;

    try {
      const response = await fetch(`${API_BASE_URL}/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        this.user = data.user;
        this.isAuthenticated = true;
        this.isAdmin = data.user.is_admin;
        return data.user;
      }

    } catch (error) {
      console.error('Get current user failed:', error);
    }

    return null;
  }

  clearAuthState() {
    this.token = null;
    this.user = null;
    this.isAuthenticated = false;
    this.isAdmin = false;
    localStorage.removeItem('auth_token');
    // Also clear any session storage
    sessionStorage.clear();
  }

  getAuthHeaders() {
    if (!this.token) return {};
    return {
      'Authorization': `Bearer ${this.token}`,
    };
  }

  // Check if current user can access admin panel
  canAccessAdmin() {
    const canAccess = this.isAuthenticated && this.isAdmin;
    return canAccess;
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;